/**
 * The browser-safe half of a Roca sorter batch: the CSV, and how one row is
 * classified once the catalog matches are already in hand. The database lookup
 * and the quantity write live in `roca-batch.ts`, which the dashboard cannot
 * import.
 */

export const ROCA_PRINTINGS = ["Normal", "Foil", "Holofoil", "Reverse Holofoil"] as const;
export type RocaPrinting = (typeof ROCA_PRINTINGS)[number];

/** The sorter's category list. It confirms which game the file is; matching does not use it. */
export const ROCA_CATEGORY_IDS = ["POKEMON", "MTG", "ONE_PIECE", "LORCANA"] as const;

export const ROCA_CATEGORIES = [
  { id: "POKEMON", label: "Pokemon" },
  { id: "MTG", label: "MTG" },
  { id: "ONE_PIECE", label: "OP" },
  { id: "LORCANA", label: "Lorcana" },
] as const;

/** Boxed pulled a card when `assets[0].price` was above this many cents ($200). */
export const ROCA_HIGH_VALUE_CENTS = 20_000;

/** Boxed stored this when a Roca id was cleared. It is not an id. */
export const ROCA_UNAVAILABLE_ID = "Unavailable";

export const MAX_ROCA_BATCH_ROWS = 2_000;

/**
 * The batch name Retool asked for when saving a file. The same name on the same
 * CSV replaces that file.
 */
export const ROCA_BATCH_NAMES = [
  "Charmander",
  "Pikachu",
  "Bulbasaur",
  "Squirtle",
  "Jolteon",
  "Umbreon",
  "Eevee",
  "Mudkip",
  "Litten",
  "Rayquaza",
  "Dragonite",
  "Piplup",
  "Sylveon",
  "Gengar",
  "Garchomp",
  "Snorlax",
  "Psyduck",
  "Ditto",
  "Heracross",
  "???",
] as const;
export type RocaBatchName = (typeof ROCA_BATCH_NAMES)[number];

/** One matched card stored in a saved batch. High-value cards are left out. */
export interface RocaSavedEntry {
  itemId: string;
  delta: number;
  productName: string;
  setName: string;
  tcgplayerId: string;
  rarity: string;
  number: string;
  catalogName: string;
  value: number | null;
  quantity: number;
  printing: string | null;
  imageUrl: string | null;
  rocaId: string | null;
  archived: boolean;
}

/** A review list stops here. A list this long is not treated as the only match. */
export const ROCA_CANDIDATE_CAP = 25;

export interface RocaBatchRow {
  /** 1-based record number in the file. */
  line: number;
  productName: string;
  setName: string;
  /** The sorter's TCGplayer Id column: a Roca id, or "Unavailable". */
  tcgplayerId: string;
  quantity: number;
  rarity: string;
  number: string;
}

export interface RocaMatchCandidate {
  id: string;
  name: string;
  collectionName: string | null;
  printing: string | null;
  quantity: number;
  value: number | null;
  imageUrl: string | null;
  rocaId: string | null;
  archived: boolean;
}

export interface RocaMatchResult extends RocaBatchRow {
  item: RocaMatchCandidate | null;
  /** Other cards the row could be, when there is no single match. */
  candidates: RocaMatchCandidate[];
  /** True when the candidate list stopped at {@link ROCA_CANDIDATE_CAP}. */
  candidatesCapped: boolean;
  matchedBy: "roca_id" | "name" | "partial" | null;
  highValue: boolean;
}

export interface ParsedRocaCsv {
  rows: RocaBatchRow[];
  rowIssues: { line: number; message: string }[];
  /** Problems with the file itself. When one is set, `rows` is empty. */
  fileIssues: string[];
}

const COLUMNS = [
  { key: "productName", header: "product name", label: "Product Name" },
  { key: "setName", header: "set name", label: "Set Name" },
  { key: "tcgplayerId", header: "tcgplayer id", label: "TCGplayer Id" },
  { key: "quantity", header: "add to quantity", label: "Add to Quantity" },
  { key: "rarity", header: "rarity", label: "Rarity" },
  { key: "number", header: "number", label: "Number" },
] as const;

type ColumnKey = (typeof COLUMNS)[number]["key"];

const REQUIRED_COLUMNS = COLUMNS.filter(
  (column) => column.key !== "rarity" && column.key !== "number",
);

/** A Roca id worth storing. Blank and boxed's cleared value are not. */
export function rocaIdToStore(csvId: string, current: string | null): string | null {
  const id = csvId.trim();
  if (!id || id === ROCA_UNAVAILABLE_ID || id === current) return null;
  return id;
}

export function rocaHighValue(value: number | null): boolean {
  return (value ?? 0) > ROCA_HIGH_VALUE_CENTS;
}

/** The sorter's CSV. A quoted field may contain commas. */
export function parseRocaCsv(text: string): ParsedRocaCsv {
  const records = parseCsvRecords(text).filter(
    (cells) => !cells.every((cell) => cell.trim() === ""),
  );
  const header = records[0];
  if (!header) return { rows: [], rowIssues: [], fileIssues: ["The file has no header row."] };

  const indexes = headerIndexes(header);
  if (!indexes) {
    const missing = REQUIRED_COLUMNS.filter(
      (column) => !header.some((cell) => normalizeHeader(cell) === column.header),
    ).map((column) => column.label);
    return {
      rows: [],
      rowIssues: [],
      fileIssues: [`The file needs these columns: ${missing.join(", ")}.`],
    };
  }

  const rows: RocaBatchRow[] = [];
  const rowIssues: { line: number; message: string }[] = [];
  records.slice(1).forEach((cells, index) => {
    const line = index + 2;
    const productName = cellAt(cells, indexes, "productName");
    const setName = cellAt(cells, indexes, "setName");
    const tcgplayerId = cellAt(cells, indexes, "tcgplayerId");
    const quantityCell = cellAt(cells, indexes, "quantity");
    if (!productName && !setName && !tcgplayerId && !quantityCell) return;
    if (!/^-?\d+$/.test(quantityCell)) {
      rowIssues.push({ line, message: "Add to Quantity must be a whole number." });
      return;
    }
    const quantity = Number.parseInt(quantityCell, 10);
    if (quantity < -100_000 || quantity > 100_000) {
      rowIssues.push({ line, message: "Add to Quantity is out of range." });
      return;
    }
    if (!productName && rocaIdToStore(tcgplayerId, null) === null) {
      rowIssues.push({ line, message: "This row has no card name or Roca id." });
      return;
    }
    rows.push({
      line,
      productName,
      setName,
      tcgplayerId,
      quantity,
      rarity: cellAt(cells, indexes, "rarity"),
      number: cellAt(cells, indexes, "number"),
    });
  });

  if (rows.length > MAX_ROCA_BATCH_ROWS) {
    return {
      rows: [],
      rowIssues: [],
      fileIssues: [`A batch can add at most ${MAX_ROCA_BATCH_ROWS.toLocaleString()} rows.`],
    };
  }
  return { rows, rowIssues, fileIssues: [] };
}

interface NameLists {
  sameName: RocaMatchCandidate[];
  loose: RocaMatchCandidate[];
  nameOnly: RocaMatchCandidate[];
}

/**
 * One sorter row against the cards a search already found. `sameName` is every
 * item with that exact name. `loose` is name and set containing the row at the
 * chosen printing. `nameOnly` is used when `loose` is empty.
 */
export function decideRocaMatch(
  row: RocaBatchRow,
  printing: string,
  rocaMatch: RocaMatchCandidate | null,
  lists: NameLists,
): RocaMatchResult {
  const rocaId = rocaIdToStore(row.tcgplayerId, null);
  if (rocaMatch && rocaId) return matched(row, rocaMatch, "roca_id");
  if (!row.productName.trim()) return unmatched(row);

  const strict = lists.sameName.filter(
    (item) =>
      sameText(item.name, row.productName) &&
      sameText(item.collectionName, row.setName) &&
      samePrinting(item.printing, printing),
  );
  const strictHit = strict[0];
  if (strict.length === 1 && strictHit) return matched(row, strictHit, "name");

  const loose = lists.loose.filter(
    (item) =>
      includesText(item.name, row.productName) &&
      includesText(item.collectionName, row.setName) &&
      samePrinting(item.printing, printing),
  );
  const looseHit = loose[0];
  if (loose.length === 1 && looseHit) return matched(row, looseHit, "partial");
  if (loose.length > 1) return review(row, loose, loose.length >= ROCA_CANDIDATE_CAP);

  const nameOnly = lists.nameOnly.filter((item) => includesText(item.name, row.productName));
  if (nameOnly.length > 0) return review(row, nameOnly, nameOnly.length >= ROCA_CANDIDATE_CAP);
  return unmatched(row);
}

function matched(
  row: RocaBatchRow,
  item: RocaMatchCandidate,
  matchedBy: "roca_id" | "name" | "partial",
): RocaMatchResult {
  return {
    ...row,
    item,
    candidates: [],
    candidatesCapped: false,
    matchedBy,
    highValue: rocaHighValue(item.value),
  };
}

function review(
  row: RocaBatchRow,
  candidates: RocaMatchCandidate[],
  capped: boolean,
): RocaMatchResult {
  return {
    ...row,
    item: null,
    candidates: candidates
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, ROCA_CANDIDATE_CAP),
    candidatesCapped: capped,
    matchedBy: null,
    highValue: false,
  };
}

function unmatched(row: RocaBatchRow): RocaMatchResult {
  return {
    ...row,
    item: null,
    candidates: [],
    candidatesCapped: false,
    matchedBy: null,
    highValue: false,
  };
}

function sameText(value: string | null, expected: string): boolean {
  return (value ?? "").trim().toLowerCase() === expected.trim().toLowerCase();
}

function includesText(value: string | null, needle: string): boolean {
  const term = needle.trim().toLowerCase();
  if (!term) return false;
  return (value ?? "").toLowerCase().includes(term);
}

function samePrinting(value: string | null, printing: string): boolean {
  return (value ?? "").trim() === printing.trim();
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function headerIndexes(cells: string[]): Map<ColumnKey, number> | null {
  const indexes = new Map<ColumnKey, number>();
  cells.forEach((cell, index) => {
    const header = normalizeHeader(cell);
    const column = COLUMNS.find((candidate) => candidate.header === header);
    if (column && !indexes.has(column.key)) indexes.set(column.key, index);
  });
  const missing = REQUIRED_COLUMNS.some((column) => !indexes.has(column.key));
  return missing ? null : indexes;
}

function cellAt(cells: string[], indexes: Map<ColumnKey, number>, key: ColumnKey): string {
  const index = indexes.get(key);
  return (index === undefined ? "" : (cells[index] ?? "")).trim();
}

/** RFC 4180 records. A quoted field may contain commas and line breaks. */
function parseCsvRecords(text: string): string[][] {
  const source = text.replace(/^\uFEFF/, "");
  const records: string[][] = [];
  let record: string[] = [];
  let cell = "";
  let quoted = false;

  for (let index = 0; index < source.length; index++) {
    const char = source[index];
    if (quoted) {
      if (char === '"') {
        if (source[index + 1] === '"') {
          cell += '"';
          index++;
        } else {
          quoted = false;
        }
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"' && cell.length === 0) {
      quoted = true;
      continue;
    }
    if (char === ",") {
      record.push(cell);
      cell = "";
      continue;
    }
    if (char === "\n" || char === "\r") {
      if (char === "\r" && source[index + 1] === "\n") index++;
      record.push(cell);
      cell = "";
      records.push(record);
      record = [];
      continue;
    }
    cell += char ?? "";
  }

  if (cell.length > 0 || record.length > 0) {
    record.push(cell);
    records.push(record);
  }
  return records;
}

/**
 * Retool's S3 key: `roca/` plus the CSV name without `.csv`, then the batch name.
 * A slash in the file name is dropped so the key stays one object under `roca/`.
 */
export function rocaBatchFileKey(fileName: string, batchName: string): string {
  const base = fileName.split(".csv")[0]?.replace(/[\\/]/g, "").trim() || "batch";
  return `roca/${base.slice(0, 180)}-${batchName}`;
}

/** The cards to add, and the ones to pull, using the value stored with the file. */
export function splitRocaSavedEntries(entries: RocaSavedEntry[]): {
  ready: RocaSavedEntry[];
  high: RocaSavedEntry[];
} {
  const ready: RocaSavedEntry[] = [];
  const high: RocaSavedEntry[] = [];
  for (const entry of entries) {
    if (rocaHighValue(entry.value)) high.push(entry);
    else ready.push(entry);
  }
  ready.sort((a, b) => a.catalogName.localeCompare(b.catalogName));
  return { ready, high };
}
